import _ from 'lodash';
import Combinatorics from 'js-combinatorics';

import { STATS } from 'shared/stats';
import { SLOT_MAP } from 'shared/constants';

import {
  classIdMap,
  getSpecsByMainStat,
  getSpecInfo,
  getSpecInfoById,
  getCharacterSpecInfo,
} from 'shared/character';

import GameData from 'shared/gameData';
import { addTag } from 'shared/itemTags';
import { deepFreeze } from 'shared/util';

export const slotMap = SLOT_MAP;

export const qualityNameMap = {
  0: 'poor',
  1: 'common',
  2: 'uncommon',
  3: 'rare',
  4: 'epic',
  5: 'legendary',
  6: 'artifact',
  7: 'heirloom',
};

export const SOCKET_TYPES = {
  PRISMATIC: 'PRISMATIC',
  DOMINATION: 'DOMINATION',
  PRIMORDIAL: 'PRIMORDIAL',
  TINKER: 'TINKER',
};

export const FISHING_POLE = {
  itemClass: 2,
  itemSubClass: 20,
};

export const INVALID_WEAPON_TYPES = [{
  itemClass: 2,
  itemSubClass: 14,
}];

const invTypeToHands = {
  13: '1h', // Weapon
  14: 'oh', // Shield
  15: '2h', // Ranged (Bow, XBow, Gun)
  17: '2h', // Two-Hand
  21: 'mh', // Main Hand
  22: 'oh', // Off Hand
  23: 'oh', // Holdable (Tome)
  26: 'mh', // MH Ranged (Wand / Gun)

  // 25 - Thrown (removed)
  // 27 - Quiver
  // 28 - Relic
};


// overrides for a few cases
const weaponClassDetailsRaw = [{
  // 1h axe
  itemClass: 2,
  itemSubClass: 0,
  specsCanDrop: [
    // str
    ['paladin', 'protection'],
    ['warrior', 'protection'],
    ['warrior', 'fury'],
    ['deathknight', 'frost'],

    // int
    ['paladin', 'holy'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['monk', 'mistweaver'],
    ['evoker', 'augmentation'],
    ['evoker', 'devastation'],
    ['evoker', 'preservation'],

    // agi
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'assassination'],
  ],
}, {
  // 2h axe
  itemClass: 2,
  itemSubClass: 1,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
    ['hunter', 'survival'],
  ],
}, {
  // gun
  itemClass: 2,
  itemSubClass: 2,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // gun
  itemClass: 2,
  itemSubClass: 3,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // 1h mace
  itemClass: 2,
  itemSubClass: 4,
  specsCanDrop: [
    // tanks
    ['paladin', 'protection'],
    ['warrior', 'fury'],
    ['warrior', 'protection'],
    ['deathknight', 'frost'],

    // casters/healers
    ['paladin', 'holy'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['monk', 'mistweaver'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // agi
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
}, {
  // 2h mace
  itemClass: 2,
  itemSubClass: 5,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
    ['shaman', 'elemental'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['evoker', 'preservation'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
  ],
}, {
  // polearm
  itemClass: 2,
  itemSubClass: 6,
  specsCanDrop: [
    // agi
    ['druid', 'feral'],
    ['druid', 'guardian'],
    ['hunter', 'survival'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],

    // str
    ['paladin', 'retribution'],
    ['warrior', 'arms'],
    ['warrior', 'fury'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
  specsAlsoCanUse: [
    ['druid', 'balance'],
  ],
}, {
  // 1h sword
  itemClass: 2,
  itemSubClass: 7,
  specsCanDrop: [
    // tank
    ['paladin', 'protection'],
    ['warrior', 'fury'],
    ['warrior', 'protection'],
    ['deathknight', 'frost'],

    // agi sword
    ['rogue', 'outlaw'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // int sword
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['paladin', 'holy'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'assassination'],
  ],
}, {
  // 2h sword
  itemClass: 2,
  itemSubClass: 8,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
}, {
  // warglaive
  itemClass: 2,
  itemSubClass: 9,
  specsCanDrop: [
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
  ],
}, {
  // staff
  itemClass: 2,
  itemSubClass: 10,
  specsCanDrop: [
    // int
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // agi
    ['druid', 'feral'],
    ['druid', 'guardian'],
    ['hunter', 'survival'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
  specsAlsoCanUse: [
    ['warrior', 'arms'],
    ['warrior', 'fury'],
  ],
}, {
  // fist
  itemClass: 2,
  itemSubClass: 13,
  specsCanDrop: [
    // agi
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],

    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],
  ],
  specsAlsoCanUse: [
    ['shaman', 'elemental'],
  ],
}, {
  // dagger
  itemClass: 2,
  itemSubClass: 15,
  specsCanDrop: [
    // int
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // agi
    ['rogue', 'assassination'],
    ['rogue', 'subtlety'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'outlaw'],
  ],
}, {
  // bow/crossbow
  itemClass: 2,
  itemSubClass: 18,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // wand
  itemClass: 2,
  itemSubClass: 19,
  hand: 'mh',
  specsCanDrop: [
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
  ],
}, {
  // misc (off hands, tomes)
  itemClass: 4,
  itemSubClass: 0,
  hand: 'oh',
  noEnchant: true,
  specsCanDrop: [
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['paladin', 'holy'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],
  ],
  specsAlsoCanUse: [
    ['shaman', 'elemental'],
  ],
}, {
  // shield
  itemClass: 4,
  itemSubClass: 6,
  hand: 'oh',
  noEnchant: true,
  specsCanDrop: [
    ['paladin', 'holy'],
    ['paladin', 'protection'],
    ['warrior', 'protection'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
  ],
}];

//////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// Bonus ID Helpers
//
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getBonusIds = item => {
  if (item.bonus_id) {
    return _.map(_.filter(_.split(item.bonus_id, '/')), bid => parseInt(bid, 10));
  }

  if (item.bonusLists) {
    return item.bonusLists;
  }

  return [];
};

export const hasBonusId = (item, bonusId) => {
  return _.includes(getBonusIds(item), bonusId);
};

export const setBonusIds = (item, bonusIdArray) => {
  return {
    ...item,
    bonus_id: _.join(bonusIdArray, '/'),
    bonusLists: bonusIdArray,
  };
};

export const addBonusIds = (item, bonusIds) => {
  if (!item || !bonusIds) {
    return item;
  }
  const origIds = getBonusIds(item);
  const bonusIdsArray = !_.isArray(bonusIds) ? [bonusIds] : bonusIds;
  return setBonusIds(item, [...origIds, ...bonusIdsArray]);
};

export const isItemLevelBonus = (bonusId) => {
  return Boolean(GameData.BonusLevels[bonusId]);
};

export const isBaseLevelBonus = (bonusId) => {
  return Boolean(GameData.BonusBaseLevels[bonusId]);
};

export const isSocketBonus = (bonusId) => {
  return Boolean(GameData.BonusSockets[bonusId]);
};

export const getItemLevelAdjust = (bonusIdArray) => {
  const bonusId = _.last(_.filter(bonusIdArray, isItemLevelBonus));
  return GameData.BonusLevels[bonusId];
};

export const getItemBonusForDelta = levelDelta => GameData.BonusLevelDeltas[levelDelta];

export const getBaseLevelFromBonuses = bonusIds => {
  const baseLevelBonuses = _.filter(_.map(bonusIds, bid => GameData.BonusBaseLevels[bid]));
  return _.minBy(baseLevelBonuses, 'base_level_priority')?.base_level || null;
};

export const getItemBaseLevel = item => {
  const bonusIds = getBonusIds(item);

  return getBaseLevelFromBonuses(bonusIds) || item.baseItemLevel || item.itemLevel;
};

export const removeItemLevelBonuses = (item) => {
  if (!item) {
    return item;
  }
  const origIds = getBonusIds(item);
  return setBonusIds(item, _.reject(origIds, isItemLevelBonus));
};

export const updateItemLevel = (item, newItemLevel) => {
  let newItem = { ...item };

  const baseItemLevel = getItemBaseLevel(item);

  // remove item level bonuses
  let bonusIds = getBonusIds(newItem);
  bonusIds = _.reject(bonusIds, isItemLevelBonus);

  // update the base item level if there are bonus IDs with base_level

  // determine offset
  const newOffset = newItemLevel - baseItemLevel;

  // find correct bonus ID
  const newBonusId = GameData.BonusLevelDeltas[newOffset];

  bonusIds.push(parseInt(newBonusId, 10));

  newItem = setBonusIds(newItem, bonusIds);
  // save base item level if not already extant
  if (!newItem.baseItemLevel) {
    newItem.baseItemLevel = baseItemLevel || newItem.itemLevel;
  }
  newItem.itemLevel = newItemLevel;

  return newItem;
};

const getUpgradeSetArray = _.memoize(() => {
  return _.flatten(_.values(GameData.BonusUpgradeSets));
});

let seasonBonusLevelLookup;
const getSeasonBonusLevelLookup = () => {
  if (!seasonBonusLevelLookup) {
    seasonBonusLevelLookup = {};
    const upgradeBonuses = _.filter(getUpgradeSetArray(), 'seasonId');
    _.each(upgradeBonuses, upgrade => {
      const { seasonId } = upgrade;
      if (seasonId) {
        if (!seasonBonusLevelLookup[seasonId]) {
          seasonBonusLevelLookup[seasonId] = {};
        }
        seasonBonusLevelLookup[seasonId][upgrade.itemLevel] = upgrade.bonusId;
      }
    });
  }
  return seasonBonusLevelLookup;
};
export const getSeasonBonusForLevel = (season, level) => {
  const lookup = getSeasonBonusLevelLookup();
  if (lookup[season]) {
    return seasonBonusLevelLookup[season][level] || null;
  }
  return null;
};

export const getSeasonMaxLevel = season => {
  const lookup = getSeasonBonusLevelLookup();
  return _.max(_.keys(lookup[season]));
};

let bonusUpgradeSetLookup;
export const getBonusUpgradeSet = (bonusId) => {
  if (!bonusUpgradeSetLookup) {
    bonusUpgradeSetLookup = _.mapValues(
      _.keyBy(getUpgradeSetArray(), 'bonusId'),
      entry => GameData.BonusUpgradeSets[entry.group],
    );
  }

  return bonusUpgradeSetLookup[bonusId];
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getItemSlotName = item => SLOT_MAP[item.inventoryType];

const weaponClassDetails = _.map(weaponClassDetailsRaw, details => {
  const specsCanDrop = _.map(details.specsCanDrop, spec => _.get(getSpecInfo(spec[0], spec[1]), 'id'));
  const specsCanUse = _.union(
    specsCanDrop,
    _.map(details.specsAlsoCanUse, spec => _.get(getSpecInfo(spec[0], spec[1]), 'id')),
  );
  return {
    ...details,
    specsCanDrop,
    specsCanUse,
  };
});

export const getWeaponHands = (weapon) => {
  // make sure we have an item
  if (!weapon || !weapon.itemClass) {
    return null;
  }
  // reject items that are not in the main inv type weapon list (filters out rings)
  if (!invTypeToHands[weapon.inventoryType]) {
    return null;
  }
  const override = _.find(weaponClassDetails, { itemClass: weapon.itemClass, itemSubClass: weapon.itemSubClass });
  return _.get(override, 'hand', invTypeToHands[weapon.inventoryType]);
};

export const isWeaponEnchantable = weapon => {
  const info = _.find(weaponClassDetails, { itemClass: weapon.itemClass, itemSubClass: weapon.itemSubClass });
  if (info && info.noEnchant) {
    return false;
  }
  if (weapon.quality === 6) {
    return false;
  }
  return true;
};

export const LEGION_LEGENDARY_CATEGORY = 357;

export const invertedQualityNameMap = _.mapValues(_.invert(qualityNameMap), parseInt);

export const invertedSlotMap = _.mapValues(_.invert(slotMap), slotId => parseInt(slotId, 10));

export const armorSubClasses = [{
  itemSubClass: 0,
  key: 'misc',
  name: 'Misc',
}, {
  itemSubClass: 1,
  key: 'cloth',
  name: 'Cloth',
}, {
  itemSubClass: 2,
  key: 'leather',
  name: 'Leather',
}, {
  itemSubClass: 3,
  key: 'mail',
  name: 'Mail',
}, {
  itemSubClass: 4,
  key: 'plate',
  name: 'Plate',
}, {
  itemSubClass: 6,
  key: 'shield',
  name: 'Shield',
}];

const MAIN_HAND_SLOT = invertedSlotMap.main_hand;

const mainStatMap = {
  agi: ['STAT_AGILITY', 'STAT_AGI_INT', 'STAT_STR_AGI', 'STAT_STR_AGI_INT'],
  int: ['STAT_INTELLECT', 'STAT_AGI_INT', 'STAT_STR_INT', 'STAT_STR_AGI_INT'],
  str: ['STAT_STRENGTH', 'STAT_STR_AGI', 'STAT_STR_INT', 'STAT_STR_AGI_INT'],
};

const mainStatIdMap = _.mapValues(
  mainStatMap,
  statEnums => _.map(statEnums, enumName => _.get(_.find(STATS, { enum: enumName }), 'dataId')),
);

const allMainStats = _.flatten(_.values(mainStatIdMap));

const itemHasAnyMainStat = item => {
  let hasStat = false;
  _.each(item.stats, stat => {
    hasStat = hasStat || _.includes(allMainStats, stat.id);
  });
  return hasStat;
};

const itemHasMainStat = (item, mainStatShortName) => {
  let hasStat = false;
  _.each(item.stats, stat => {
    hasStat = hasStat || _.includes(mainStatIdMap[mainStatShortName], stat.id);
  });
  return hasStat;
};

export const isCloak = item => {
  return item.itemClass === 4 && item.inventoryType === parseInt(invertedSlotMap.back, 10);
};

// Test if an item is usable or if it can drop
// This is private since it has the ugly boolean parameter
// Use the exported isItemUsable or doesItemDrop below
export const itemNotUsableReason = (item, classSearch, specSearch, onlyDrop = false, checkSpec = true) => {
  const specInfo = getSpecInfo(classSearch, specSearch);

  ///////////////////////////////////////////////////
  // check class/spec constraints first

  if (!specInfo) {
    return 'no_spec_info';
  }

  if (item.allowableClasses) {
    if (!_.includes(item.allowableClasses, specInfo.class.id)) {
      return 'class_cannot_use';
    }
  }

  // check spec override first
  if (checkSpec && item.specs) {
    if (_.includes(item.specs, specInfo.id)) {
      return null;
    }
    return 'spec_cannot_use';
  }

  ///////////////////////////////////////////////////
  // Now check various attributes of the armor itself

  let wrongMainStat = false;

  // check for non-matching main stat on an item
  if (item.stats && itemHasAnyMainStat(item) && !itemHasMainStat(item, specInfo.mainStat)) {
    wrongMainStat = true;
  }

  // jewelry is usable by anyone assuming no class/spec constraints
  if (_.includes(['finger', 'neck'], getItemSlotName(item))) {
    // if jewelry has a main stat, make sure it's the right one.
    // some very old jewelry showing up in refactored M+ dungeons have main stat
    // These will hopefully go away completely when Blizz updates the dungeon journal
    if (wrongMainStat) {
      return 'wrong_main_stat';
    }
    return null;
  }

  // check armor class
  if (item.itemClass === 4) {
    // skip cloaks, anyone can use
    if (item.inventoryType !== parseInt(invertedSlotMap.back, 10)) {
      const armorSubClass = _.find(armorSubClasses, { itemSubClass: item.itemSubClass });
      if (armorSubClass) {
        if (!_.includes(specInfo.class.armorTypes, armorSubClass.key)) {
          return 'wrong_armor_type';
        }
      }
    }
  }

  // check weapon/spec table
  if (_.includes(['main_hand', 'off_hand'], getItemSlotName(item))) {
    const weaponInfo = _.find(weaponClassDetails, { itemClass: item.itemClass, itemSubClass: item.itemSubClass });
    if (weaponInfo) {
      if (onlyDrop) {
        if (!_.includes(weaponInfo.specsCanDrop, specInfo.id)) {
          return 'item_does_not_drop';
        }
      } else if (!_.includes(weaponInfo.specsCanUse, specInfo.id)) {
        return 'spec_cannot_use';
      }
    }
  }

  // if there is no main stat at all, probably anyone can use the item.
  // This was initially an issue with Azshara's Font of Power but several SL items appear to use the asme
  // item categories that mess up the old analysis
  if (item.stats && !itemHasAnyMainStat(item)) {
    return null;
  }

  if (wrongMainStat) {
    return 'wrong_main_stat';
  }

  return null;
};

export const isItemUsable = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, false, false);
};

export const isItemIntended = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, false);
};

export const doesItemDrop = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, true);
};

const enchantInfo = (id, customDisplayName) => {
  const enchant = _.find(GameData.Enchantments, { id });
  const name = customDisplayName || enchant.displayName;
  return {
    ...enchant,
    key: enchant.tokenizedName,
    displayName: name,
    name,
    type: enchant.socketType,
  };
};

const enchantInfoWithCraftingQualties = ({
  slot,
  shortName,
  startEnchantId,
  group,
  preferred,
  maxQuality = 3,
  reverseQualityOrder = false,
  ...rest
}) => {
  const result = [];

  let ranks = _.range(1, maxQuality + 1);
  if (reverseQualityOrder) {
    ranks = _.reverse(ranks);
  }

  for (let i = 0; i < ranks.length; i += 1) {
    const rank = ranks[i];
    let pref;
    if (rank === maxQuality) {
      pref = preferred;
    }
    result.push({
      slot,
      shortName: `${shortName}${rank}`,
      group: `${group}_${rank}`,
      preferred: pref,
      ...enchantInfo(startEnchantId + i),
      ...rest,
    });
  }

  return result;
};

const prefGroup = {
  all: { roles: ['melee', 'caster', 'ranged', 'tank', 'healer'] },
  notRanged: { roles: ['melee', 'caster', 'tank', 'healer'] },
  meleeRange: { roles: ['melee', 'tank'] },
  melee: { roles: ['melee'] },
  healer: { roles: ['healer'] },
  tank: { roles: ['tank'] },
  ranged: { roles: ['ranged'] },
  caster: { roles: ['caster'] },
  agi: { specs: _.map(getSpecsByMainStat('agi'), 'id') },
  int: { specs: _.map(getSpecsByMainStat('int'), 'id') },
  str: { specs: _.map(getSpecsByMainStat('str'), 'id') },
  agistr: {
    specs: _.concat(
      _.map(getSpecsByMainStat('agi'), 'id'),
      _.map(getSpecsByMainStat('str'), 'id'),
    ),
  },
  hunter: { classes: [classIdMap.Hunter] },
  deathknight: { classes: [classIdMap['Death Knight']] },
  deathknight2h: { classes: [classIdMap['Death Knight']], hands: '2h' },
};

export const getEnchants = _.memoize(() => [{
  ////////////////////////////////////////////////
  //
  // DEATH KNIGHT
  //
  ////////////////////////////////////////////////

  // Rune of the Apocalypse
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(6245),
  shortName: 'Apoc',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.meleeRange,
  group: 'death_knight',
}, {
  // Rune of Hysteria
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(6243),
  shortName: 'Hyst',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.meleeRange,
  group: 'death_knight',
}, {
  // Rune of Sanguination
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(6241),
  shortName: 'Sang',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.meleeRange,
  group: 'death_knight',
}, {
  // Rune of Spellwarding
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(6242),
  shortName: 'Ward',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.tank,
  group: 'death_knight',
}, {
  // Rune of Unending Thirst
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(6244),
  shortName: 'Thirst',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.meleeRange,
  group: 'death_knight',
}, {
  // Rune of Razorice
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(3370),
  shortName: 'Razor',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.melee,
  group: 'death_knight',
}, {
  // Rune of the Fallen Crusader
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(3368),
  shortName: 'Fallen',
  requirement: prefGroup.deathknight,
  preferred: prefGroup.meleeRange,
  group: 'death_knight',
}, {
  // Rune of the Stoneskin Gargoyle
  slot: MAIN_HAND_SLOT,
  ...enchantInfo(3847),
  shortName: 'Stone',
  requirement: prefGroup.deathknight2h,
  preferred: prefGroup.tank,
  group: 'death_knight',
},

////////////////////////////////////////////////
//
// DRAGONFLIGHT
//
////////////////////////////////////////////////

//
// WEAPON -----------
//

// Dreaming Devotion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'Dream',
  startEnchantId: 7001,
  group: 'devotion',
  preferred: prefGroup.healer,
}),

// Shadowflame Wreathe
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'SWre',
  startEnchantId: 6825,
  group: 'devotion',
  preferred: prefGroup.notRanged,
}),

// Burning Devotion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'BDev',
  startEnchantId: 6629,
  group: 'devotion',
  preferred: prefGroup.healer,
}),
// Burning Writ
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'BWrit',
  startEnchantId: 6626,
  group: 'writ',
  preferred: prefGroup.notRanged,
}),
// Earten Devotion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'EDev',
  startEnchantId: 6635,
  group: 'devotion',
  preferred: prefGroup.tank,
}),
// Earthen Writ
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'EWrit',
  startEnchantId: 6632,
  group: 'writ',
  preferred: prefGroup.notRanged,
}),
// Sophic Devotion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'SDev',
  startEnchantId: 6641,
  group: 'devotion',
  preferred: prefGroup.notRanged,
}),
// Sophic Writ
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'SWrit',
  startEnchantId: 6638,
  group: 'writ',
  preferred: prefGroup.notRanged,
}),
// Frozen Devotion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'FDev',
  startEnchantId: 6647,
  group: 'devotion',
  preferred: prefGroup.meleeRange,
}),
// Frozen Writ
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'FWrit',
  startEnchantId: 6644,
  group: 'writ',
  preferred: prefGroup.notRanged,
}),
// Wafting Devotion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'WDev',
  startEnchantId: 6653,
  group: 'devotion',
  preferred: prefGroup.notRanged,
}),
// Wafting Writ
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'WWrit',
  startEnchantId: 6650,
  group: 'writ',
  preferred: prefGroup.notRanged,
}),

// SCOPES

// High Intensity Thermal Scanner
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'HITS',
  startEnchantId: 6526,
  group: 'df-engineering',
  requirement: prefGroup.hunter,
  preferred: prefGroup.ranged,
}),
// Projectile Propulsion Pinion
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'PPP',
  startEnchantId: 6523,
  group: 'df-engineering',
  requirement: prefGroup.hunter,
  preferred: prefGroup.ranged,
}),
// Gyroscopic Kaleidoscope
...enchantInfoWithCraftingQualties({
  slot: MAIN_HAND_SLOT,
  shortName: 'GK',
  startEnchantId: 6520,
  group: 'df-engineering',
  requirement: prefGroup.hunter,
  preferred: prefGroup.ranged,
}),

//
// RING -----------
//

// Devotion of Crit
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Crit',
  startEnchantId: 6548,
  group: 'devotion',
  preferred: prefGroup.all,
}),
// Devotion of Haste
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Haste',
  startEnchantId: 6554,
  group: 'devotion',
  preferred: prefGroup.all,
}),
// Devotion of Mastery
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Mast',
  startEnchantId: 6560,
  group: 'devotion',
  preferred: prefGroup.all,
}),
// Devotion of Vers
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Vers',
  startEnchantId: 6566,
  group: 'devotion',
  preferred: prefGroup.all,
}),
// Writ of Crit
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Crit',
  startEnchantId: 6545,
  group: 'writ',
}),
// Writ of Haste
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Haste',
  startEnchantId: 6551,
  group: 'writ',
}),
// Writ of Mastery
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Mast',
  startEnchantId: 6557,
  group: 'writ',
}),
// Writ of Vers
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.finger,
  shortName: 'Vers',
  startEnchantId: 6563,
  group: 'writ',
}),

//
// CLOAK ENCHANTS ------
//

// Graceful avoidance
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.back,
  shortName: 'Avoid',
  startEnchantId: 6590,
  group: 'tertiary',
  zeroDps: true,
}),
// Homebound Speed
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.back,
  shortName: 'Speed',
  startEnchantId: 6602,
  group: 'tertiary',
  zeroDps: true,
}),
// Regenerative Leech
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.back,
  shortName: 'Leech',
  startEnchantId: 6596,
  group: 'tertiary',
  zeroDps: true,
}),
// writ of avoidance
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.back,
  shortName: 'Avoid',
  startEnchantId: 6587,
  group: 'writ',
  zeroDps: true,
}),
// writ of leech
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.back,
  shortName: 'Leech',
  startEnchantId: 6593,
  group: 'writ',
  zeroDps: true,
}),
// writ of speed
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.back,
  shortName: 'Speed',
  startEnchantId: 6599,
  group: 'writ',
  zeroDps: true,
}),

//
// CHEST ENCHANTS ---------
//

// Accelerated Agility
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.chest,
  shortName: 'Agi',
  startEnchantId: 6614,
  group: 'stats',
  preferred: prefGroup.agi,
}),
// Reserve of Intellect
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.chest,
  shortName: 'Int',
  startEnchantId: 6617,
  group: 'stats',
  preferred: prefGroup.int,
}),
// Sustained Strength
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.chest,
  shortName: 'Str',
  startEnchantId: 6620,
  group: 'stats',
  preferred: prefGroup.str,
}),
// Waking Stats
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.chest,
  shortName: 'Stats',
  startEnchantId: 6623,
  group: 'stats',
  preferred: prefGroup.all,
}),

//
// BRACER ENCHANTS ------
//

// Devotion of Avoidance
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.wrist,
  shortName: 'Avoid',
  startEnchantId: 6572,
  group: 'devotion',
  zeroDps: true,
}),
// Devotion of Leech
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.wrist,
  shortName: 'Leech',
  startEnchantId: 6578,
  group: 'devotion',
  zeroDps: true,
}),
// Devotion of Speed
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.wrist,
  shortName: 'Speed',
  startEnchantId: 6584,
  group: 'devotion',
  zeroDps: true,
}),
// Writ of Avoidance
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.wrist,
  shortName: 'Avoid',
  startEnchantId: 6569,
  group: 'writ',
  zeroDps: true,
}),
// Writ of Leech
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.wrist,
  shortName: 'Leech',
  startEnchantId: 6575,
  group: 'writ',
  zeroDps: true,
}),
// Writ of Speed
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.wrist,
  shortName: 'Speed',
  startEnchantId: 6581,
  group: 'writ',
  zeroDps: true,
}),


//
// BOOT ENCHANTS --------
//

// Plainsrunner's Breeze
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.feet,
  shortName: 'Move',
  startEnchantId: 6605,
  group: 'speed',
  zeroDps: true,
}),
// Rider's Reassurance
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.feet,
  shortName: 'Mount',
  startEnchantId: 6608,
  group: 'speed',
  zeroDps: true,
}),
// Watcher's Loam
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.feet,
  shortName: 'Stam',
  startEnchantId: 6611,
  group: 'stam',
  zeroDps: true,
}),

//
// LEG ENCHANTS --------
//

// Lambent Armor Kit
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Lamb',
  startEnchantId: 6828,
  group: 'armorkit',
  preferred: prefGroup.all,
}),
// Fierce Armor Kit
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Fierce',
  startEnchantId: 6488,
  group: 'armorkit',
  preferred: prefGroup.agistr,
}),
// Frosted Armor Kit
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Frstd',
  startEnchantId: 6494,
  group: 'armorkit',
  preferred: prefGroup.agistr,
}),
// Reinforced Armor Kit
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Reinf',
  startEnchantId: 6491,
  group: 'armorkit',
  preferred: prefGroup.agistr,
}),

// Frozen Spellthread
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Frzn',
  startEnchantId: 6539,
  group: 'spellthread',
  preferred: prefGroup.int,
}),
// Temporal Spellthread
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Tmprl',
  startEnchantId: 6542,
  group: 'spellthread',
  preferred: prefGroup.int,
}),
// Vibrant Spellthread
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.legs,
  shortName: 'Vbrnt',
  startEnchantId: 6536,
  group: 'spellthread',
  preferred: prefGroup.int,
}),

//
// BELT ENCHANTS --------
//

// 10.1
// Shadowed Belt Clasp
...enchantInfoWithCraftingQualties({
  slot: invertedSlotMap.waist,
  shortName: 'Shdw',
  startEnchantId: 6904,
  reverseQualityOrder: true, // in game data it's 3 2 1, not 1 2 3
  group: 'waist',
  preferred: prefGroup.all,
}),

//
// HELM ENCHANTS --------
//

// 10.2
{
  // Incandescent Essence
  ...enchantInfo(7052, 'Incandescent Essence'),
  slot: invertedSlotMap.head,
  shortName: 'InEs',
  group: 'head',
  preferred: prefGroup.all,
},

//
// SHADOWLANDS STUFF
//

// Shadowlands Weapon Enchants
{
  slot: MAIN_HAND_SLOT,
  name: 'Ascended Vigor',
  shortName: 'Vigor',
  key: 'ascended_vigor',
  id: 6227,
  itemId: 172365,
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Celestial Guidance',
  shortName: 'Celest',
  key: 'celestial_guidance',
  id: 6229,
  itemId: 172366,
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Eternal Grace',
  shortName: 'Grace',
  key: 'eternal_grace',
  id: 6226,
  itemId: 172367,
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Lightless Force',
  shortName: 'Force',
  key: 'lightless_force',
  id: 6223,
  itemId: 172370,
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Sinful Revelation',
  shortName: 'Sin',
  key: 'sinful_revelation',
  id: 6228,
  itemId: 172368,
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Optical Target Embiggener',
  shortName: 'Optic',
  key: 'optical_target_embiggener',
  id: 6196,
  icon: 'inv_engineering_90_scope_blue',
  itemId: 172920,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  group: 'engineering',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Infra-green Reflex Sight',
  shortName: 'Infra',
  key: 'infragreen_reflex_sight',
  id: 6195,
  icon: 'inv_engineering_90_scope_green',
  itemId: 172921,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  group: 'engineering',
}, {

  // RING

  // Shadowlands Ring Enchants

  slot: 11,
  name: '16 Critical Strike',
  shortName: 'Crit',
  key: '16crit',
  id: 6164,
  itemId: 172361,
  group: 'tenet',
  stat: {
    type: 'crit',
    amount: 16,
  },
}, {
  slot: 11,
  name: '16 Haste',
  shortName: 'Haste',
  key: '16haste',
  id: 6166,
  itemId: 172362,
  group: 'tenet',
  stat: {
    type: 'haste',
    amount: 16,
  },
}, {
  slot: 11,
  name: '16 Mastery',
  shortName: 'Mast',
  key: '16mastery',
  id: 6168,
  itemId: 172363,
  group: 'tenet',
  stat: {
    type: 'mastery',
    amount: 16,
  },
}, {
  slot: 11,
  name: '16 Versatility',
  shortName: 'Vers',
  key: '16vers',
  id: 6170,
  itemId: 172364,
  group: 'tenet',
  stat: {
    type: 'vers',
    amount: 16,
  },
}, {
  slot: 11,
  name: '12 Critical Strike',
  shortName: 'Crit',
  key: '12crit',
  id: 6163,
  itemId: 172357,
  group: 'bargain',
  stat: {
    type: 'crit',
    amount: 12,
  },
}, {
  slot: 11,
  name: '12 Haste',
  shortName: 'Haste',
  key: '12haste',
  id: 6165,
  itemId: 172358,
  group: 'bargain',
  stat: {
    type: 'haste',
    amount: 12,
  },
}, {
  slot: 11,
  name: '12 Mastery',
  shortName: 'Mastery',
  key: '12mastery',
  id: 6167,
  itemId: 172359,
  group: 'bargain',
  stat: {
    type: 'mastery',
    amount: 12,
  },
}, {
  slot: 11,
  name: '12 Versatility',
  shortName: 'Vers',
  key: '12vers',
  id: 6169,
  itemId: 172360,
  group: 'bargain',
  stat: {
    type: 'vers',
    amount: 12,
  },
}, {


  // CLOAK ENCHANTS

  slot: invertedSlotMap.back,
  name: 'Fortified Avoidance',
  shortName: 'Avoid',
  key: 'fortified_avoidance',
  id: 6203,
  itemId: 172411,
  group: 'stam',
}, {
  slot: invertedSlotMap.back,
  name: 'Fortified Leech',
  shortName: 'Leech',
  key: 'fortified_leech',
  id: 6204,
  itemId: 172412,
  group: 'stam',
}, {
  slot: invertedSlotMap.back,
  name: 'Fortified Speed',
  key: 'fortified_speed',
  shortName: 'Speed',
  id: 6202,
  itemId: 172410,
  group: 'stam',
}, {
  slot: invertedSlotMap.back,
  name: 'Soul Vitality',
  shortName: 'Stam',
  key: 'soul_vitality',
  id: 6208,
  itemId: 177660,
  group: 'stam',
}, {

  // CHEST ENCHANTS

  slot: invertedSlotMap.chest,
  name: 'Eternal Bounds',
  shortName: 'Int',
  key: 'eternal_bounds',
  id: 6217,
  itemId: 177715,
  group: 'stats',
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Bulwark',
  shortName: 'Bulw',
  key: 'eternal_bulwark',
  id: 6213,
  itemId: 172418,
  group: 'stats',
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Skirmish',
  shortName: 'Skir',
  key: 'eternal_skirmish',
  id: 6214,
  itemId: 177659,
  group: 'stats',
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Stats',
  shortName: 'Stats',
  key: 'eternal_stats',
  id: 6230,
  itemId: 177962,
  group: 'stats',
}, {
  slot: invertedSlotMap.chest,
  name: 'Sacred Stats',
  shortName: 'Stats',
  key: 'sacred_stats',
  id: 6216,
  itemId: 177716,
  group: 'stats',
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Insight',
  shortName: 'Ins',
  key: 'eternal_insight',
  id: 6265,
  itemId: 183738,
  group: 'stats',

  // BRACER ENCHANTS

}, {
  slot: invertedSlotMap.wrist,
  name: 'Eternal Intellect',
  shortName: 'Int',
  key: 'eternal_intellect',
  id: 6220,
  itemId: 172415,
  group: 'stats',
}, {
  slot: invertedSlotMap.wrist,
  name: 'Illuminated Soul',
  shortName: 'Int',
  key: 'illuminated_soul',
  id: 6219,
  itemId: 172414,
  group: 'stats',
}, {
  slot: invertedSlotMap.wrist,
  name: 'Shaded Hearthing',
  shortName: 'Hrth',
  key: 'shaded_hearthing',
  id: 6222,
  itemId: 172416,
  group: 'stats',
  zeroDps: true,

  // GLOVE ENCHANTS

}, {
  slot: invertedSlotMap.hands,
  name: 'Eternal Strength',
  shortName: 'Str',
  key: 'eternal_strength',
  id: 6210,
  itemId: 172408,
  group: 'stats',
}, {
  slot: invertedSlotMap.hands,
  name: 'Strength of Soul',
  shortName: 'Str',
  key: 'strength_of_soul',
  id: 6209,
  itemId: 172407,
  group: 'stats',
}, {
  slot: invertedSlotMap.hands,
  name: 'Shadowlands Gathering',
  shortName: 'Gthr',
  key: 'shadowlands_gathering',
  id: 6205,
  itemId: 172406,
  group: 'stats',
  zeroDps: true,

  // BOOT ENCHANTS

}, {
  slot: invertedSlotMap.feet,
  name: 'Eternal Agility',
  shortName: 'Agi',
  key: 'eternal_agility',
  id: 6211,
  itemId: 172419,
  group: 'stats',
}, {
  slot: invertedSlotMap.feet,
  name: 'Agile Soulwalker',
  shortName: 'Agi',
  key: 'agile_soulwalker',
  id: 6212,
  itemId: 172413,
  group: 'stats',
}, {
  slot: invertedSlotMap.feet,
  name: 'Soul Treads',
  shortName: 'Fall',
  key: 'soul_treads',
  id: 6207,
  itemId: 177661,
  group: 'stats',
  zeroDps: true,
}]);

export const getSocketBonuses = _.memoize(() => _.union(
  [1808], // default socket bonus ID to use when making faking sockets
  _.map(_.keys(GameData.BonusSockets), b => parseInt(b, 10)),
));
/*
[
  1808,
  4802,
  523,
];
*/

export const secondaryBonuses = [{
  bonusId: 603,
  stat: 'crit',
  name: 'Crit',
}, {
  bonusId: 604,
  stat: 'haste',
  name: 'Haste',
}, {
  bonusId: 605,
  stat: 'mastery',
  name: 'Mastery',
}, {
  bonusId: 607,
  stat: 'vers',
  name: 'Versatility',
}];


export const getCraftedStats = item => {
  // check bonus IDs first
  const bonusIds = getBonusIds(item);
  const craftedStatsBonuses = _.filter(_.map(bonusIds, bid => GameData.BonusCraftedStats[bid]));

  if (craftedStatsBonuses.length > 0) {
    if (craftedStatsBonuses.length > 1) {
      throw new Error('Unexpected data - item has multiple crafted stats bonus IDs');
    }
    return craftedStatsBonuses[0];
  }

  // then check crafted_stats
  if (item.crafted_stats) {
    return _.map(_.filter(_.split(item.crafted_stats, '/')), cs => parseInt(cs, 10));
  }

  // otherwise there are no dynamic secondaries
  return null;
};

/////////////////////////////////////////////////////////////////////
//
// ... GEMS ...
//
/////////////////////////////////////////////////////////////////////

export const getGemInfo = _.memoize(() => deepFreeze([

  // 10.0.7 Primordial Gems
  {
    itemId: 204000,
    id: 204000,
    name: 'Storm Infused Stone',
    icon: 'inv_10_elementalcombinedfoozles_lightning',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6755,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204001,
    id: 204001,
    name: 'Echoing Thunder Stone',
    icon: 'inv_misc_reforgedarchstone_01',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6756,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204002,
    id: 204002,
    name: 'Flame Licked Stone',
    icon: 'inv_elemental_primal_fire',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6757,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204003,
    id: 204003,
    name: 'Raging Magma Stone',
    icon: 'inv_pet_scorchedstone',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6758,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204004,
    id: 204004,
    name: 'Searing Smokey Stone',
    icon: 'inv_misc_rune_04',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6759,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204005,
    id: 204005,
    name: 'Entropic Fel Stone',
    icon: 'spell_nature_elementalshields',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6760,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204006,
    id: 204006,
    name: 'Indomitable Earth Stone',
    icon: 'inv_elemental_primal_earth',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6761,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204007,
    id: 204007,
    name: 'Shining Obsidian Stone',
    icon: 'inv_stone_15',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6762,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204009,
    id: 204009,
    name: 'Gleaming Iron Stone',
    icon: 'inv_ore_mithril_01',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6764,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204010,
    id: 204010,
    name: 'Deluging Water Stone',
    icon: 'inv_elemental_primal_water',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6765,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204011,
    id: 204011,
    name: 'Freezing Ice Stone',
    icon: 'spell_frost_frozencore',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6766,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204012,
    id: 204012,
    name: 'Cold Frost Stone',
    icon: 'inv_enchant_shardshadowfrostlarge',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6767,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204013,
    id: 204013,
    name: 'Exuding Steam Stone',
    icon: 'inv_10_elementalcombinedfoozles_water',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6768,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204014,
    id: 204014,
    name: 'Sparkling Mana Stone',
    icon: 'inv_elemental_primal_mana',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6769,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204015,
    id: 204015,
    name: 'Swirling Mojo Stone',
    icon: 'inv_misc_gem_ebondraenite_02',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6770,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204018,
    id: 204018,
    name: 'Humming Arcane Stone',
    icon: 'inv_enchant_essencearcanelarge',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6773,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204019,
    id: 204019,
    name: 'Harmonic Music Stone',
    icon: 'inv_enchant_shardprismaticlarge',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6774,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204020,
    id: 204020,
    name: 'Wild Spirit Stone',
    icon: 'inv_elemental_primal_life',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6775,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204021,
    id: 204021,
    name: 'Necromantic Death Stone',
    icon: 'inv_ore_saronite_01',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6776,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204022,
    id: 204022,
    name: 'Pestilent Plague Stone',
    icon: 'inv_infernalbrimstone',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6777,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204025,
    id: 204025,
    name: 'Obscure Pastel Stone',
    icon: 'inv_enchant_prismaticsphere',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6780,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204027,
    id: 204027,
    name: 'Desirous Blood Stone',
    icon: 'inv_misc_gem_bloodstone_02',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6782,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204029,
    id: 204029,
    name: 'Prophetic Twilight Stone',
    icon: 'spell_fire_twilightfire',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6784,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },
  {
    itemId: 204030,
    id: 204030,
    name: 'Wind Sculpted Stone',
    icon: 'inv_elemental_primal_air',
    quality: 4,
    type: 'PRIMORDIAL',
    enchantId: 6722,
    itemLevel: 411,
    group: 'primordial',
    preferred: prefGroup.all,
    slot: 'socket',
  },


  // Engineering tinkers -----------------------
  {
    itemId: 198289,
    id: 198289,
    name: 'Tinker: Alarm-O-Turret',
    icon: 'inv_10_engineering2_tinkermodules_color1',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 569,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6505,
  },
  {
    itemId: 198290,
    id: 198290,
    name: 'Tinker: Alarm-O-Turret',
    icon: 'inv_10_engineering2_tinkermodules_color1',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 569,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6505,
  },
  {
    itemId: 198291,
    id: 198291,
    name: 'Tinker: Alarm-O-Turret',
    icon: 'inv_10_engineering2_tinkermodules_color1',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 569,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6505,
  },
  {
    itemId: 198295,
    id: 198295,
    name: 'Tinker: Breath of Neltharion',
    icon: 'inv_10_engineering2_tinkermodules_color3',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 571,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6508,
  },
  {
    itemId: 198296,
    id: 198296,
    name: 'Tinker: Breath of Neltharion',
    icon: 'inv_10_engineering2_tinkermodules_color3',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 571,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6508,
  },
  {
    itemId: 198297,
    id: 198297,
    name: 'Tinker: Breath of Neltharion',
    icon: 'inv_10_engineering2_tinkermodules_color3',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 571,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6508,
  },
  {
    itemId: 198298,
    id: 198298,
    name: 'Tinker: Plane Displacer',
    icon: 'inv_10_engineering2_tinkermodules_color6',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 573,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6504,
  },
  {
    itemId: 198299,
    id: 198299,
    name: 'Tinker: Plane Displacer',
    icon: 'inv_10_engineering2_tinkermodules_color6',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 573,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6504,
  },
  {
    itemId: 198300,
    id: 198300,
    name: 'Tinker: Plane Displacer',
    icon: 'inv_10_engineering2_tinkermodules_color6',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 573,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6504,
  },
  {
    itemId: 198301,
    id: 198301,
    name: 'Tinker: Supercollide-O-Tron',
    icon: 'inv_10_engineering2_tinkermodules_color5',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 575,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6502,
  },
  {
    itemId: 198302,
    id: 198302,
    name: 'Tinker: Supercollide-O-Tron',
    icon: 'inv_10_engineering2_tinkermodules_color5',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 575,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6502,
  },
  {
    itemId: 198303,
    id: 198303,
    name: 'Tinker: Supercollide-O-Tron',
    icon: 'inv_10_engineering2_tinkermodules_color5',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 575,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6502,
  },
  {
    itemId: 198304,
    id: 198304,
    name: 'Tinker: Grounded Circuitry',
    icon: 'inv_10_engineering2_tinkermodules_color2',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 572,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6507,
  },
  {
    itemId: 198305,
    id: 198305,
    name: 'Tinker: Grounded Circuitry',
    icon: 'inv_10_engineering2_tinkermodules_color2',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 572,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6692,
  },
  {
    itemId: 198306,
    id: 198306,
    name: 'Tinker: Grounded Circuitry',
    icon: 'inv_10_engineering2_tinkermodules_color2',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 572,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6693,
  },
  {
    itemId: 199188,
    id: 199188,
    name: 'Tinker: Polarity Amplifier',
    icon: 'inv_10_engineering2_tinkermodules_color4',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 574,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6535,
  },
  {
    itemId: 199189,
    id: 199189,
    name: 'Tinker: Polarity Amplifier',
    icon: 'inv_10_engineering2_tinkermodules_color4',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 574,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6535,
  },
  {
    itemId: 199190,
    id: 199190,
    name: 'Tinker: Polarity Amplifier',
    icon: 'inv_10_engineering2_tinkermodules_color4',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 574,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6535,
  },
  {
    itemId: 201407,
    id: 201407,
    name: 'Tinker: Arclight Vital Correctors',
    icon: 'inv_10_engineering_device_gadget1_color2',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 570,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6691,
  },
  {
    itemId: 201408,
    id: 201408,
    name: 'Tinker: Arclight Vital Correctors',
    icon: 'inv_10_engineering_device_gadget1_color2',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 570,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6691,
  },
  {
    itemId: 201409,
    id: 201409,
    name: 'Tinker: Arclight Vital Correctors',
    icon: 'inv_10_engineering_device_gadget1_color2',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 570,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6691,
  },
  {
    itemId: 205014,
    id: 205014,
    name: 'Tinker: Shadowflame Rockets',
    icon: 'inv_10_engineering2_tinkermodules_color3',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 573,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6831,
  },
  {
    itemId: 205015,
    id: 205015,
    name: 'Tinker: Shadowflame Rockets',
    icon: 'inv_10_engineering2_tinkermodules_color3',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 573,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6831,
  },
  {
    itemId: 205016,
    id: 205016,
    name: 'Tinker: Shadowflame Rockets',
    icon: 'inv_10_engineering2_tinkermodules_color3',
    quality: 3,
    unique: true,
    itemLimit: {
      category: 573,
      quantity: 1,
    },
    type: 'TINKER',
    enchantId: 6831,
  },

  // Primalist Gems -----------------------

  // Inscribed Illimited Diamond
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6470,
    shortName: 'Pri/C',
    group: 'df-primalist',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Fierce Illimited Diamond
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6398,
    shortName: 'Pri/H',
    group: 'df-primalist',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Skillful Illimited Diamond
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6473,
    shortName: 'Pri/M',
    group: 'df-primalist',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Resplendent Illimited Diamond
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6476,
    shortName: 'Pri/V',
    group: 'df-primalist',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),



  // Crit Gems -----------------------
  // Deadly Alexstraszite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6419,
    shortName: 'Crit',
    group: 'df-crit',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Crafty Alexstraszite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6395,
    shortName: 'C/H',
    group: 'df-crit',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Sensei's Alexstraszite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6413,
    shortName: 'C/M',
    group: 'df-crit',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Radiant Alexstraszite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6416,
    shortName: 'C/V',
    group: 'df-crit',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Craft Queen's Ruby
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6392,
    shortName: 'C/H',
    group: 'df-crit',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
  }),

  // Haste Gems -----------------------
  // Quick Ysemerald
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6443,
    shortName: 'Haste',
    group: 'df-haste',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Crafy Ysemerald
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6440,
    shortName: 'H/C',
    group: 'df-haste',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Keen Ysemerald
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6437,
    shortName: 'H/M',
    group: 'df-haste',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Energized Ysemerald
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6434,
    shortName: 'H/V',
    group: 'df-haste',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Energized Vibrant Emerald
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6404,
    shortName: 'H/V',
    group: 'df-haste',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
  }),

  // Mast Gems -----------------------
  // Fractured Neltharite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6455,
    shortName: 'Mast',
    group: 'df-mast',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Sensei's Neltharite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6452,
    shortName: 'M/C',
    group: 'df-mast',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Keen Neltharite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6449,
    shortName: 'M/H',
    group: 'df-mast',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Zen Neltharite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6446,
    shortName: 'M/V',
    group: 'df-mast',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Sensei's Sundered Onyx
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6407,
    shortName: 'M/C',
    group: 'df-mast',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
  }),

  // Vers Gems -----------------------
  // Stormy Malygite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6431,
    shortName: 'Vers',
    group: 'df-vers',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Radiant Malygite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6428,
    shortName: 'V/C',
    group: 'df-vers',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Energized Malygite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6422,
    shortName: 'V/H',
    group: 'df-vers',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Zen Malygite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6425,
    shortName: 'V/M',
    group: 'df-vers',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.all,
  }),
  // Zen Mystic Sapphire
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6401,
    shortName: 'V/M',
    group: 'df-vers',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
  }),

  // Stam Gems -----------------------
  // Jagged Nozdorite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6458,
    shortName: 'Sta/C',
    group: 'df-stam',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.tank,
  }),

  // Forceful Nozdorite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6461,
    shortName: 'Sta/H',
    group: 'df-stam',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.tank,
  }),
  // Puissant Nozdorite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6464,
    shortName: 'Sta/M',
    group: 'df-stam',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.tank,
  }),
  // Stady Nozdorite
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6467,
    shortName: 'Sta/V',
    group: 'df-stam',
    dps: true,
    type: SOCKET_TYPES.PRISMATIC,
    preferred: prefGroup.tank,
  }),
  // Solid Eternity Amber
  ...enchantInfoWithCraftingQualties({
    slot: 'socket',
    startEnchantId: 6410,
    shortName: 'Stam',
    group: 'df-stam',
    dps: false,
    type: SOCKET_TYPES.PRISMATIC,
  }),



  //////////////////////////////////////////////////
  // Shadowlands Gems
  //////////////////////////////////////////////////

  {
    shortName: 'speed',
    name: 'Speed',
    itemId: 173126,
    id: 173126,
    quality: 2,
    //unique: false,
    icon: 'inv_jewelcrafting_90_cutuncommon_green',
    group: 'sl-uncommon',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '16crit',
    name: '16 Crit',
    itemId: 173127,
    id: 173127,
    quality: 3,
    //unique: false,
    icon: 'inv_jewelcrafting_90_rarecut_orange',
    group: 'sl-rare',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'crit',
      amount: 16,
    },
  }, {
    shortName: '16haste',
    name: '16 Haste',
    itemId: 173128,
    id: 173128,
    quality: 3,
    //unique: false,
    icon: 'inv_jewelcrafting_90_rarecut_yellow',
    group: 'sl-rare',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'haste',
      amount: 16,
    },
  }, {
    shortName: '16mastery',
    name: '16 Mastery',
    itemId: 173130,
    id: 173130,
    quality: 3,
    //unique: false,
    icon: 'inv_jewelcrafting_90_rarecut_purple',
    group: 'sl-rare',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'mastery',
      amount: 16,
    },
  }, {
    shortName: '16vers',
    name: '16 Vers',
    itemId: 173129,
    id: 173129,
    quality: 3,
    //unique: false,
    icon: 'inv_jewelcrafting_90_rarecut_blue',
    group: 'sl-rare',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'vers',
      amount: 16,
    },
  }, {

    shortName: '12crit',
    name: '12 Crit',
    itemId: 173121,
    id: 173121,
    quality: 2,
    //unique: false,
    icon: 'inv_jewelcrafting_90_cutuncommon_orange',
    group: 'sl-uncommon',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'crit',
      amount: 12,
    },
  }, {
    shortName: '12haste',
    name: '12 Haste',
    itemId: 173122,
    id: 173122,
    quality: 2,
    //unique: false,
    icon: 'inv_jewelcrafting_90_cutuncommon_yellow',
    group: 'sl-uncommon',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'haste',
      amount: 12,
    },
  }, {
    shortName: '12mastery',
    name: '12 Mastery',
    itemId: 173124,
    id: 173124,
    quality: 2,
    //unique: false,
    icon: 'inv_jewelcrafting_90_cutuncommon_purple',
    group: 'sl-uncommon',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'mastery',
      amount: 12,
    },
  }, {
    shortName: '12vers',
    name: '12 Vers',
    itemId: 173123,
    id: 173123,
    quality: 2,
    //unique: false,
    icon: 'inv_jewelcrafting_90_cutuncommon_blue',
    group: 'sl-uncommon',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'vers',
      amount: 12,
    },
  }, {

    // JC Gems

    shortName: '7agi',
    name: '7 Agility',
    itemId: 168637,
    id: 168637,
    quality: 4,
    unique: 468,
    icon: 'inv_misc_metagem_b',
    group: 'main_stat',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'agi',
      amount: 7,
    },
  }, {
    shortName: '7int',
    name: '7 Intellect',
    itemId: 168638,
    id: 168638,
    quality: 4,
    unique: 468,
    icon: 'inv_misc_metagem_b',
    group: 'main_stat',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'int',
      amount: 7,
    },
  }, {
    shortName: '7str',
    name: '7 Strength',
    itemId: 168636,
    id: 168636,
    quality: 4,
    unique: 468,
    icon: 'inv_misc_metagem_b',
    group: 'main_stat',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'str',
      amount: 7,
    },

  }, {

    shortName: '6agi',
    name: '6 Agility',
    itemId: 153708,
    id: 153708,
    quality: 4,
    unique: 463,
    icon: 'inv_jewelcrafting_80_specialgemcut01',
    group: 'main_stat',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'agi',
      amount: 6,
    },
  }, {
    shortName: '6int',
    name: '6 Intellect',
    itemId: 153709,
    id: 153709,
    quality: 4,
    unique: 463,
    icon: 'inv_jewelcrafting_80_specialgemcut01',
    group: 'main_stat',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'int',
      amount: 6,
    },
  }, {
    shortName: '6str',
    name: '6 Strength',
    itemId: 153707,
    id: 153707,
    quality: 4,
    unique: 463,
    icon: 'inv_jewelcrafting_80_specialgemcut01',
    group: 'main_stat',
    type: SOCKET_TYPES.PRISMATIC,
    stat: {
      type: 'str',
      amount: 6,
    },

  }, {

    shortName: '7crit',
    name: '7 Crit',
    itemId: 168639,
    id: 168639,
    quality: 4,
    // unique: false,
    icon: 'inv_misc_gem_x4_uncommon_perfectcut_orange',
    group: 'epic',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '7haste',
    name: '7 Haste',
    itemId: 168641,
    id: 168641,
    quality: 4,
    // unique: false,
    icon: 'inv_misc_gem_x4_uncommon_perfectcut_yellow',
    group: 'epic',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '7mastery',
    name: '7 Mastery',
    itemId: 168640,
    id: 168640,
    quality: 4,
    // unique: false,
    icon: 'inv_misc_gem_x4_uncommon_perfectcut_purple',
    group: 'epic',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '7vers',
    name: '7 Vers',
    itemId: 168642,
    id: 168642,
    quality: 4,
    // unique: false,
    icon: 'inv_misc_gem_x4_uncommon_perfectcut_blue',
    group: 'epic',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '6crit',
    name: '6 Crit',
    itemId: 154126,
    id: 154126,
    quality: 3,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem02_orange',
    group: 'rare',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '6haste',
    name: '6 Haste',
    itemId: 154127,
    id: 154127,
    quality: 3,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem02_yellow',
    group: 'rare',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '6mastery',
    name: '6 Mastery',
    itemId: 154129,
    id: 154129,
    quality: 3,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem02_purple',
    group: 'rare',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '6vers',
    name: '6 Vers',
    itemId: 154128,
    id: 154128,
    quality: 3,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem02_blue',
    group: 'rare',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '4crit',
    name: '4 Crit',
    itemId: 153710,
    id: 153710,
    quality: 2,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem01_orange',
    group: 'uncommon',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '4haste',
    name: '4 Haste',
    itemId: 153711,
    id: 153711,
    quality: 2,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem01_yellow',
    group: 'uncommon',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '4mastery',
    name: '4 Mastery',
    itemId: 153713,
    id: 153713,
    quality: 2,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem01_purple',
    group: 'uncommon',
    type: SOCKET_TYPES.PRISMATIC,
  }, {
    shortName: '4vers',
    name: '4 Vers',
    itemId: 153712,
    id: 153712,
    quality: 2,
    // unique: false,
    icon: 'inv_jewelcrafting_80_cutgem01_blue',
    group: 'uncommon',
    type: SOCKET_TYPES.PRISMATIC,
  },
]));

export const getEnhancements = _.memoize(() => _.union(
  getEnchants(),
  _.map(getGemInfo(), gem => ({
    ...gem,
    slot: 'socket',
  })),
));

export const findEnhancement = enhancementId => _.find(getEnhancements(), { id: enhancementId });

export const getUniqueGems = () => (
  _.fromPairs(_.map(_.filter(getGemInfo(), g => g.unique), g => [g.shortName, g.name]))
);
export const getNormalGems = () => (
  _.fromPairs(_.map(_.filter(getGemInfo(), g => !g.unique), g => [g.shortName, g.name]))
);

export const getGems = () => _.fromPairs(_.map(_.filter(getGemInfo(), 'shortName'), g => [g.shortName, g.name]));

export const getGemDetails = gemId => _.find(getGemInfo(), { itemId: parseInt(gemId, 10) });
export const getGemDetailsByEnchantId = id => _.find(getGemInfo(), { id });
export const isGemUnique = gemId => _.get(getGemDetails(gemId), 'unique', false);

export const getEnchantableSlots = _.memoize(() => _.union(
  // need to look up all the inventor types that this slot _could_ use
  // This is the ol' chestnut of multiple inv types used for chest slot
  _.flatten(_.map(getEnchants(), e => {
    return _.map(_.keys(_.pickBy(slotMap, v => v === slotMap[e.slot])), k => parseInt(k, 10));
  })),
  _.map(
    _.keys(_.pickBy(slotMap, s => _.includes(['main_hand', 'off_hand'], s))),
    s => parseInt(s, 10),
  ),
));

export const enhancementsForSlot = slot => _.filter(getEnhancements(), { slot });
export const enhancementsForSlotName = slotName => enhancementsForSlot(invertedSlotMap[slotName]);

export const getEnhancementsForSlotNameAndSpec = (slotName, specInfo) => {
  if (!slotName || !specInfo) {
    return [];
  }
  const classInfo = specInfo.class;
  let slotEnhancements;
  if (slotName === 'socket') {
    slotEnhancements = enhancementsForSlot('socket');
  } else {
    slotEnhancements = enhancementsForSlotName(slotName === 'off_hand' ? 'main_hand' : slotName);
  }
  return _.sortBy(
    _.filter(slotEnhancements, enchant => {
      // check for class requirement (e.g., DK weapon enchants)
      const requiredClasses = _.get(enchant, 'requirement.classes', null);
      if (requiredClasses) {
        // reject if no class info or if character is not the required class
        if (!classInfo || !_.includes(requiredClasses, classInfo.id)) {
          return false;
        }
      }

      return true;
    }),
    enhancement => _.get(enhancement, 'requirement.classes', Infinity),
  );
};

export const isPreferredEnhancement = (enhancement, specInfo) => {
  const preferredRole = _.includes(_.get(enhancement, 'preferred.roles'), specInfo.role);
  const preferredSpec = _.includes(_.get(enhancement, 'preferred.specs'), specInfo.id);

  const preferred = preferredRole || preferredSpec;

  // DKs only want DK weapon enchants
  if (enhancement.slot === MAIN_HAND_SLOT && _.includes([250, 251, 252], specInfo.id)) {
    return preferred && enhancement.group === 'death_knight';
  }

  return preferred;
};
export const getPreferredEnhancements = (slot, specInfo) => {
  return _.filter(
    getEnhancementsForSlotNameAndSpec(slot, specInfo),
    enhancement => isPreferredEnhancement(enhancement, specInfo),
  );
};

// aliases
export const getEnchant = id => _.find(getEnchants(), { id });
export const enchantsForSlot = enhancementsForSlot;
export const enchantsForSlotName = enhancementsForSlotName;
export const getEnchantsForSlotNameAndSpec = getEnhancementsForSlotNameAndSpec;
export const getEnchantByTokenizedName = tokenizedName => _.find(getEnchants(), { tokenizedName });

export const enchantsForItem = item => enhancementsForSlot(
  // get the internal "canonical" slot
  invertedSlotMap[getItemSlotName(item)],
);

export const isValidEnhancementForItem = (enhancement, item) => {
  // check for correct weapon enchant handedness
  const enchant = getEnchant(enhancement.enhancementId);
  if (enchant?.requirement?.hands) {
    const hands = getWeaponHands(item);
    if (hands !== enchant.requirement.hands) {
      return false;
    }
  }

  return true;
};

export const isEnchantable = item => {
  return item && _.includes(getEnchantableSlots(), item.inventoryType);
};

export const canRollSecondary = item => (
  item
  && item.chanceBonusList
  // compare secondary bonus IDs against chance bonus list, return true if any
  // bonus changes are presetn
  && _.intersection(_.map(secondaryBonuses, sb => sb.bonusId), item.chanceBonusList).length > 0
);

export const isTrinket = item => item && getItemSlotName(item) === 'trinket';

export const isRingOrTrinket = item => (
  item && _.includes(['finger', 'trinket'], getItemSlotName(item))
);

export const defaultSlot = (item) => {
  const slotName = getItemSlotName(item);
  if (isRingOrTrinket(item)) {
    return `${slotName}1`;
  }
  return slotName;
};

const CRAFT_STAT_1 = _.find(STATS, { enum: 'STAT_CRAFT_MOD_1' });
const CRAFT_STAT_2 = _.find(STATS, { enum: 'STAT_CRAFT_MOD_2' });
export const numCraftedStats = item => {
  if (!item) {
    return false;
  }

  const hasCraftStat1 = _.some(item.stats, { id: CRAFT_STAT_1.dataId });
  const hasCraftStat2 = _.some(item.stats, { id: CRAFT_STAT_2.dataId });
  return (+hasCraftStat1) + (+hasCraftStat2);
};
export const hasCraftedStats = item => {
  return numCraftedStats(item) > 0;
};
export const craftedStatsOrderMatters = item => {
  const alloc1 = _.find(item.stats, { id: CRAFT_STAT_1.dataId });
  const alloc2 = _.find(item.stats, { id: CRAFT_STAT_2.dataId });

  if (alloc1 && alloc2 && alloc1.alloc !== alloc2.alloc) {
    return true;
  }

  return false;
};

export const CRAFTED_STATS = [{
  id: 32,
  name: 'crit',
}, {
  id: 36,
  name: 'haste',
}, {
  id: 49,
  name: 'mastery',
}, {
  id: 40,
  name: 'vers',
}];

export const CRAFTED_STAT_COMBOS = _.sortBy(
  _.union(
    _.map(
      CRAFTED_STATS,
      stat => ({
        id: _.join([stat.id, stat.id], '/'),
        name: _.startCase(stat.name),
        value: stat.name,
        stats: [stat.id],
      }),
    ),
    _.map(
      Combinatorics.permutation(CRAFTED_STATS, 2).toArray(),
      combo => {
        return {
          id: _.join(_.map(combo, 'id'), '/'),
          name: _.join(_.map(combo, c => _.startCase(c.name)), '/'),
          value: _.join(_.map(combo, 'name'), ','),
          stats: _.map(combo, 'id'),
        };
      },
    ),
  ),
  'name',
);

const getSocketTypeInfo = (socketInfo, type) => {
  return socketInfo ? socketInfo[type] : null;
};

export const numStaticSockets = (item, type) => {
  if (!type) {
    throw new Error('numStaticSockets called without a socket type');
  }
  const socketInfo = getSocketTypeInfo(item?.socketInfo, type);
  if (socketInfo) {
    return socketInfo.staticSlots;
  }
  if (item?.socketInfo?.sockets) {
    return _.size(_.filter(item.socketInfo.sockets, { type }));
  }
  return 0;
};

const ROLLABLE_SOCKET_TYPES = [
  SOCKET_TYPES.PRISMATIC,
];

export const numDynamicSockets = (item, type) => {
  if (!type) {
    throw new Error('numDynamicSockets called without a socket type');
  }
  if (_.includes(ROLLABLE_SOCKET_TYPES, type)) {
    if (item && item.bonus_id) {
      const bonusIds = _.map(_.filter(_.split(item.bonus_id, '/')), b => parseInt(b, 10));
      const socketBonusIds = _.intersection(getSocketBonuses(), bonusIds);
      if (_.size(socketBonusIds) > 0) {
        return GameData.BonusSockets[_.first(socketBonusIds)];
      }
    }
  }
  return 0;
};

const ITEMS_NO_DYNAMIC_SOCKET = [
  // pocket-sized computation device
  167555,
];

export const MAX_SOCKETS = 6;

export const SOCKETABLE_SLOTS = [
  'head',
  'finger',
  'neck',
  'wrist',
  'waist',
];

export const canAddSocket = (item, type) => {
  if (!type) {
    throw new Error('canAddSocket called without a socket type');
  }

  if (!_.includes(ROLLABLE_SOCKET_TYPES, type)) {
    return false;
  }

  if (!item) {
    return false;
  }

  const socketInfo = getSocketTypeInfo(item.socketInfo, type);
  const slot = getItemSlotName(item);

  // already has a socket
  if (socketInfo?.staticSlots > 0 || socketInfo?.dynamicSlots > 0) {
    if (slot === 'neck') {
      return socketInfo.dynamicSlots < 3;
    }
    return false;
  }

  const totalSockets = _.reduce(
    item.socketInfo,
    (acc, info) => acc + info.total,
    0,
  );
  if (totalSockets >= 3) {
    return false;
  }

  // primordial gear can't roll prismatic sockets
  if (type === SOCKET_TYPES.PRISMATIC) {
    let hasOtherSocket = false;
    // full socket info data structure
    if (item.socketInfo && item.socketInfo[SOCKET_TYPES.PRIMORDIAL]?.total > 0) {
      hasOtherSocket = true;
    }
    // client-side data structure, shows in Droptimizer, probably should figure out how to get rid of this structure
    if (item.socketInfo?.sockets) {
      hasOtherSocket = _.find(item.socketInfo.sockets, { type: SOCKET_TYPES.PRIMORDIAL });
    }
    if (hasOtherSocket) {
      return false;
    }
  }

  // artifact
  if (item.quality === 6) {
    return false;
  }

  // legendary
  if (item.quality === 5) {
    // Old legendaries cannot be socketed, but shadowlands can be
    if (item.expansion < 8) {
      return false;
    }
  }

  // deny list of items that cannot be socketed even if they fulfill other constraints
  if (_.includes(ITEMS_NO_DYNAMIC_SOCKET, item.id)) {
    return false;
  }


  // shadowlands sockets are limited to head, neck, rings, bracers, and belt
  // Just apply this to everything. There was an expansion check but that was doing weird stuff
  // for timewalking items
  if (!_.includes(SOCKETABLE_SLOTS, slot)) {
    return false;
  }


  return true;
};

export const numSockets = (item, type) => {
  if (!type) {
    throw new Error('numSockets called without a socket type');
  }

  const staticSockets = numStaticSockets(item, type);
  const dynamicSockets = canAddSocket(item, type) ? 1 : 0;

  if (staticSockets) {
    return staticSockets;
  }
  return dynamicSockets;
};

export const possibleSockets = item => {
  if (!item) {
    return [];
  }

  const sockets = [];

  if (item.socketInfo) {
    _.each(
      item.socketInfo,
      info => {
        for (let i = 0; i < info.total; i += 1) {
          sockets.push(info.type);
        }
      },
    );
  }
  if (_.size(sockets) < 3 && canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
    sockets.push(SOCKET_TYPES.PRISMATIC);
  }

  const slot = getItemSlotName(item);
  if (slot === 'neck' && _.size(sockets) < 3) {
    _.each(_.range(0, _.size(sockets) - 3), () => sockets.push(SOCKET_TYPES.PRISMATIC));
  }

  return sockets;
};

export const itemSocketInfo = item => {
  const types = {};
  _.each(SOCKET_TYPES, type => {
    const itemSocketTypeInfo = item.socketInfo ? item.socketInfo[type] : null;
    // keep any static slots found from web item lookups
    // frontend item lookups don't have access to the full game data
    const staticSlots = itemSocketTypeInfo?.staticSlots || numStaticSockets(item, type);
    const dynamicSlots = numDynamicSockets(item, type);
    const gemDetails = _.filter(
      _.map(_.filter(_.split(item.gem_id, '/')), g => getGemDetails(parseInt(g, 10))),
      gem => gem && gem.type === type,
    );
    const gemIds = _.map(gemDetails, 'itemId');
    const hasUniqueGem = _.size(_.filter(gemIds, isGemUnique)) > 0;
    if (staticSlots || dynamicSlots || gemIds.length > 0) {
      types[type] = {
        type,
        staticSlots,
        dynamicSlots,
        filled: gemIds.length,
        total: _.max([staticSlots, dynamicSlots]),
        gems: gemDetails,
        gemIds,
        hasUnique: hasUniqueGem,
      };
    }
  });
  return types;
};

export const getItemSocketBonusId = item => {
  const bonusIds = item.bonus_id ? _.split(item.bonus_id, '/') : item.bonusLists;
  const socketBonusId = _.first(_.filter(
    bonusIds,
    bid => {
      return GameData.BonusSockets[bid];
    },
  ));
  return socketBonusId;
};

export const addPrismaticSocket = (item) => {
  if (canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
    const newItem = { ...item };
    const bonusIds = _.map(_.filter(_.split(item.bonus_id, '/')), bid => parseInt(bid, 10));
    let newBonusIds = [...bonusIds];
    const curSocketBonusId = _.first(_.filter(bonusIds, bid => GameData.BonusSockets[bid]));
    if (!curSocketBonusId) {
      newBonusIds.push(_.first(getSocketBonuses()));
    } else {
      const newSocketNum = GameData.BonusSockets[curSocketBonusId] + 1;
      const newSocketBonusId = _.findKey(GameData.BonusSockets, num => num === newSocketNum);
      newBonusIds = _.without(newBonusIds, curSocketBonusId);
      newBonusIds.push(newSocketBonusId);
    }

    newItem.bonus_id = _.join(newBonusIds, '/');

    newItem.socketInfo = itemSocketInfo(newItem);

    return newItem;
  }

  return item;
};

export const removePrismaticSocket = item => {
  const curSocketsNum = numDynamicSockets(item, SOCKET_TYPES.PRISMATIC);
  const newSocketsNum = curSocketsNum - 1;

  let newItem = { ...item };

  const bonusIds = getBonusIds(newItem);

  const newBonusIds = _.reject(bonusIds, isSocketBonus);

  if (newSocketsNum > 0) {
    const newSocketBonusId = _.findKey(GameData.BonusSockets, n => n === newSocketsNum);
    newBonusIds.push(newSocketBonusId);
  }

  newItem = setBonusIds(newItem, newBonusIds);

  const newGems = _.take(_.split(newItem.gem_id, '/'), newSocketsNum);
  newItem.gem_id = _.join(newGems, '/');
  newItem.socketInfo = itemSocketInfo(newItem);

  return newItem;
};

export const isLegionLegendary = item => {
  if (item && item.itemLimit && item.itemLimit.category) {
    const result = item && item.quality === 5 && item.itemLimit.category === LEGION_LEGENDARY_CATEGORY;
    return result;
  }
  return false;
};

// ItemRedundancySlot
// eslint-disable-next-line max-len
// https://github.com/tomrus88/BlizzardInterfaceCode/blob/master/Interface/AddOns/Blizzard_APIDocumentationGenerated/ItemConstants_MainlineDocumentation.lua
const discountSlotMap = {
  head: 0,
  neck: 1,
  shoulder: 2,
  chest: 3,
  waist: 4,
  legs: 5,
  feet: 6,
  wrist: 7,
  hands: 8,
  finger: 9,
  trinket: 10,
  back: 11,
  twohand: 12,
  mainhand: 13,
  onehandweapon: 14,
  onehandweaponsecond: 15,
  offhand: 16,
};

export const getItemUpgrades = (item, specId, character) => {
  const upgrades = [];
  if (item.upgrade) {
    const groupId = item.upgrade.group;
    const upgradeSet = GameData.BonusUpgradeSets[groupId];
    const currentUpgrade = _.find(upgradeSet, { level: item.upgrade.level });
    const specInfo = getSpecInfoById(specId);
    let totalCosts = _.isArray(item.upgradeCost) ? [...item.upgradeCost] : [];

    const slotName = getItemSlotName(item);

    // check MH/OH/1H/2H to determine right high watermark to use
    let redundancySlot;
    if (slotName === 'main_hand') {
      const hands = getWeaponHands(item);

      const map2h = {
        '2h': 'twohand',
        mh: 'mainhand',
        '1h': 'onehandweaponsecond',
      };
      redundancySlot = map2h[hands];
    } else if (slotName === 'off_hand') {
      const hands = getWeaponHands(item);

      const mapOh = {
        '1h': 'onehandweaponsecond',
        oh: 'offhand',
      };

      redundancySlot = mapOh[hands];
    } else {
      redundancySlot = slotName;
    }

    const highWatermarks = _.find(character.slotHighWatermarks, { slot: discountSlotMap[redundancySlot] });

    for (let level = item.upgrade.level + 1; level <= item.upgrade.max; level += 1) {
      // find new item group entry
      const upgradeEntry = _.find(upgradeSet, { level });

      if (upgradeEntry && !upgradeEntry.disabled) {
        const discounts = _.filter(upgradeEntry.highWatermarkDiscounts, discount => {
          if (discount.accountWide) {
            return upgradeEntry.itemLevel <= highWatermarks?.account;
          }
          return upgradeEntry.itemLevel <= highWatermarks?.character;
        });

        if (upgradeEntry.costs) {
          // find the amount based on the inventory type mask
          const upgradeCost = _.find(upgradeEntry.costs, cost => {
            // TODO: Figure out if this is _actually_ correct
            // This feels like a hack, I suspect there's some source for these flags
            if (cost.flags === 1 && specInfo.mainStat !== 'int') {
              return false;
            }
            if (cost.flags === 2 && specInfo.mainStat === 'int') {
              return false;
            }
            // flags 4 and 8 are used on PVP gear, not sure what they indicate.
            // eslint-disable-next-line no-bitwise
            return cost.mask_inv_type & (2 ** item.inventoryType);
          });
          if (upgradeCost?.amounts) {
            // eslint-disable-next-line no-loop-func
            _.each(upgradeCost.amounts, amount => {
              const search = amount.currencyId ? { currencyId: amount.currencyId } : { itemId: amount.itemId };
              const currentCost = _.find(totalCosts, search);

              let scaleAmount = 1;
              const discountInfo = _.find(discounts, d => {
                if (d.type === 'currency') {
                  return d.id === amount.currencyId;
                }
                return d.id === amount.itemId;
              });
              if (discountInfo) {
                scaleAmount = discountInfo.scaling;
              }

              if (!currentCost) {
                const finalAmount = _.round(amount.amount * scaleAmount);
                if (finalAmount) {
                  totalCosts.push({
                    ...amount,
                    amount: finalAmount,
                  });
                }
              } else {
                const finalAmount = _.round(amount.amount * scaleAmount);
                if (finalAmount) {
                  totalCosts = [
                    ..._.reject(totalCosts, search),
                    {
                      ...currentCost,
                      amount: currentCost.amount + finalAmount,
                    },
                  ];
                }
              }
            });
          } else {
            // Probably only happens on PTR with poorly constructed test items?
            // eslint-disable-next-line no-console
            //console.error('No amount found for', item.name);
          }
        }

        let bonusIds = _.filter(_.split(item.bonus_id, '/'));

        // replace upgrade bonus ID
        bonusIds = _.without(bonusIds, currentUpgrade?.bonusId);
        bonusIds = _.concat(bonusIds, upgradeEntry.bonusId);

        // figure out correct bonus ID for item level
        const curLevelBonusId = _.find(bonusIds, bid => GameData.BonusLevels[bid]) || '1472';
        const newLevelOffset = upgradeEntry.itemLevel - item.itemLevel + GameData.BonusLevels[curLevelBonusId];
        const newLevelBonusId = getItemBonusForDelta(newLevelOffset);

        bonusIds = _.without(bonusIds, curLevelBonusId);
        bonusIds = _.concat(bonusIds, newLevelBonusId);

        const upgradeItem = {
          ...item,
          upgrade: upgradeEntry,
          itemLevel: upgradeEntry.itemLevel,
          bonus_id: _.join(bonusIds, '/'),
          upgraded: true,
          upgradeCost: _.orderBy(totalCosts, ['currencyId', 'itemId']),
          equipped: false,
        };
        upgrades.push(upgradeItem);
      }
    }
  }
  return _.orderBy(upgrades, ['itemLevel'], ['desc']);
};

export const EMBELLISHED_REAGENT_SLOT_IDS = [
  112,
  123,
  178,
  179,
  180,
  226,
  233,
];


export function getReagentSlot(reagentSlotId) {
  return GameData.Crafting.slots[reagentSlotId];
}

export function getReagent(reagentId) {
  return _.find(GameData.Crafting.reagents, { id: reagentId });
}

export function getSlotForReagent(reagentId) {
  const slots = _.filter(GameData.Crafting.slots, slotInfo => {
    return _.includes(slotInfo.itemIds, reagentId);
  });
  if (slots.length > 1) {
    throw new Error('Unexpected - more than 1 possible slot for a reagent');
  }
  return _.first(slots);
}

export function getEmbellishment(embellishmentId) {
  return getReagent(embellishmentId);
}

export function getPossibleEmbellishments(item) {
  if (!item?.profession) {
    return null;
  }

  const embellishSlots = _.intersection(
    _.map(item.profession.optionalCraftingSlots, 'id'),
    EMBELLISHED_REAGENT_SLOT_IDS,
  );
  if (!embellishSlots.length) {
    return null;
  }

  const embellishments = [];
  _.each(embellishSlots, embellishSlot => {
    const slotId = _.isObject(embellishSlot) ? embellishSlot.id : embellishSlot;
    const slot = getReagentSlot(slotId);
    _.each(slot.itemIds, id => {
      const embellishment = getEmbellishment(id);
      // If there are no bonus IDs, don't include
      // This mostly applies the Griftah item that remove embellishments during recrafting
      if (_.size(embellishment.craftingBonusIds) > 0) {
        embellishments.push(embellishment);
      }
    });
  });

  return embellishments;
}

export function replaceEmbellishment(item, embellishmentId) {
  const possibleEmbels = getPossibleEmbellishments(item);
  const embelBonusIds = _.flatten(_.map(possibleEmbels, 'craftingBonusIds'));

  // remove existing embellishment bonus IDs
  const bonusIds = _.map(_.filter(_.split(item.bonus_id, '/')), bid => parseInt(bid, 10));
  const cleanedBonusIds = _.reject(bonusIds, bid => _.includes(embelBonusIds, bid));

  if (!embellishmentId) {
    return {
      ...item,
      bonus_id: _.join(cleanedBonusIds, '/'),
      // MAYBE HACK - this _could_ blow away other item limits unrelated to embellishments
      itemLimit: undefined,
    };
  }

  const targetEmbel = _.find(possibleEmbels, { id: embellishmentId });
  if (!targetEmbel) {
    throw new Error(`replaceEmbellishment cannot find embellishment ${embellishmentId}`);
  }

  const newBonusIds = [
    ...cleanedBonusIds,
    ...targetEmbel.craftingBonusIds,
  ];

  return {
    ...item,
    bonus_id: _.join(newBonusIds, '/'),
    // MAYBE HACK - this _could_ blow away other item limits unrelated to embellishments
    itemLimit: targetEmbel.itemLimit,
  };
}

const tertiaryBonusIds = [
  40,
  41,
  42,
  43,
];
const getUpgradeBonusIds = _.memoize(() => new Set(_.uniq(_.flatten(_.map(GameData.BonusUpgradeSets, bus => {
  return _.map(bus, 'bonusId');
})))));

const ITEM_CONVERSION_EXCLUDE_ITEM_IDS = new Set([
  204465, // Voice of the Silent Star
]);

export const getPossibleItemConversions = (item, specId, itemConversionId) => {
  if (ITEM_CONVERSION_EXCLUDE_ITEM_IDS.has(item.id)) {
    return [];
  }

  const itemConversion = GameData.ItemConversions[itemConversionId];
  const specInfo = getSpecInfoById(specId);

  const possibleItems = _.filter(itemConversion.items, it => {
    // can't convert an item to itself
    if (it.id === item.id) {
      return false;
    }
    if (it.allowableClasses && !_.includes(it.allowableClasses, specInfo.class.id)) {
      return false;
    }
    if (it.specs && !_.includes(it.specs, specId)) {
      return false;
    }
    if (getItemSlotName(item) !== getItemSlotName(it)) {
      return false;
    }
    return true;
  });
  return _.map(possibleItems, it => {
    let newItem = {
      ...it,
      // copy over any item upgrade info
      upgrade: item.upgrade,
      upgradeCost: item.upgradeCost,
      upgraded: item.upgraded,
      source: item.source,
      tags: item.tags,
    };

    // copy over relevant bonus IDs (tertiary, upgrade level, socket)
    const keepBonusIds = _.filter(getBonusIds(item), bonusId => {
      if (GameData.BonusSockets[bonusId]) {
        return true;
      }
      if (_.includes(tertiaryBonusIds, bonusId)) {
        return true;
      }
      if (getUpgradeBonusIds().has(bonusId)) {
        return true;
      }
      return false;
    });

    newItem = addBonusIds(newItem, keepBonusIds);

    newItem = updateItemLevel(newItem, item.itemLevel);

    return newItem;
  });
};

const RECRAFT_STAT_SLOTS = [
  125,
  224,
  225,
  227,
];


export const getStatRecrafts = item => {
  if (!item) {
    return null;
  }

  const recrafts = [];

  const statCraftingSlot = _.find(
    item.profession?.optionalCraftingSlots,
    slot => _.includes(RECRAFT_STAT_SLOTS, slot.id || slot),
  );

  const statCraftingSlotId = statCraftingSlot?.id || statCraftingSlot;

  if (statCraftingSlotId) {
    const slot = GameData.Crafting.slots[statCraftingSlotId];
    const rawPotentialBonusIds = [];
    _.each(slot.itemIds, reagentId => {
      const reagent = _.find(GameData.Crafting.reagents, { id: reagentId });
      if (_.size(reagent.craftingBonusIds)) {
        rawPotentialBonusIds.push(reagent.craftingBonusIds);
      }
    });
    const potentialBonusIds = _.uniqBy(rawPotentialBonusIds, bids => _.join(bids, '/'));
    const flatPotentialBonusIds = _.flatten(potentialBonusIds);

    const currentBonusIds = getBonusIds(item);
    const cleanBonusIds = _.reject(currentBonusIds, bid => _.includes(flatPotentialBonusIds, bid));

    _.each(potentialBonusIds, statBonusIds => {
      const itemHasThisBonus = _.size(_.intersection(currentBonusIds, statBonusIds)) > 0;
      if (!itemHasThisBonus) {
        let newItem = setBonusIds(item, cleanBonusIds);
        newItem = addBonusIds(newItem, statBonusIds);

        // look up the name of the secondary stats from this bonus ID.
        // storing it as random property on the item isn't great but I'm lazy.
        const craftedStats = GameData.BonusCraftedStats[_.first(statBonusIds)];
        const statNames = _.join(
          _.map(craftedStats, stat => _.find(STATS, { dataId: stat })?.shortName),
          ' / ',
        );
        newItem.statNames = statNames;

        recrafts.push(newItem);
      }
    });
  }


  return recrafts;
};

const CRAFTING_LEVEL_UPGRADE_SLOTS = [
  111, // Infuse with Power
  116, // Empower with Training Matrix
  126, // Infuse with Power
  189, // Empower with Training Matrix
  247, // Spark
];

const CRAFTING_SLOT_REMOVE_SOCKETS = [
  247, // any Spark craft will remove sockets since it's changing seasons
];

// map each crafted item back to the main crest
export const CRAFTING_CURRENCY_ALTERNATE = {
  // Enchanted Whelpling's Dreaming Crest
  208395: {
    id: 2706,
    amount: 30,
    type: 'currency',
  },
  // Enchanted Wyrm's Dreaming Crest
  206960: {
    id: 2708,
    amount: 45,
    type: 'currency',
  },
  // Enchanted Aspect's Dreaming Crest
  206961: {
    id: 2709,
    amount: 60,
    type: 'currency',
  },

  // Enchanted Whelpling's Shadowflame Crest
  204681: {
    id: 204193,
    amount: 1,
  },
  // Enchanted Wyrm's Shadowflame Crest
  204682: {
    id: 204196,
    amount: 3,
  },
  // Enchanted Aspect's Shadowflame Crest
  204697: {
    id: 204194,
    amount: 4,
  },
};

export const getCraftingSlotsForReagent = (itemId) => {
  return _.filter(GameData.Crafting.slots, slotInfo => _.includes(slotInfo.itemIds, itemId));
};

export const getCraftingLevelUpgrades = (item) => {
  if (!item) {
    return null;
  }

  if (!item.profession) {
    return null;
  }

  const upgrades = [];

  const itemBonusIds = getBonusIds(item);
  const itemLevelAdjust = getItemLevelAdjust(itemBonusIds);

  // Will we be back in 10.2 with multi-step requirements?
  //
  // Also this is a mess. Beware.

  _.each(item.profession.optionalCraftingSlots, slot => {
    const slotId = slot.id || slot;
    const count = slot.recraftCount || 1;
    if (_.includes(CRAFTING_LEVEL_UPGRADE_SLOTS, slotId)) {
      const slotInfo = getReagentSlot(slotId);
      _.each(slotInfo.itemIds, reagentId => {
        const reagent = getReagent(reagentId);
        const baseLevelBonuses = _.filter(_.map(reagent.craftingBonusIds, bonusId => {
          const baseLevelInfo = GameData.BonusBaseLevels[bonusId];
          if (baseLevelInfo) {
            return {
              bonusId,
              baseLevel: baseLevelInfo.base_level,
              baseLevelPriority: baseLevelInfo.base_level_priority,
            };
          }
          return null;
        }));

        // This would be weird
        if (baseLevelBonuses.length > 1) {
          throw new Error('Found more than 1 base level bonus ID for a crafted item in getCraftingLevelUpgrades!');
        }
        if (baseLevelBonuses.length === 1) {
          const { baseLevel, baseLevelPriority, bonusId } = _.first(baseLevelBonuses);
          const itemLevel = baseLevel + itemLevelAdjust;
          if (itemLevel > item.itemLevel) {
            let newBonusIds = getBonusIds(item);
            // remove bonus IDs with the same or lower base level priority.
            // This should clear out base levels from previous/older crafting steps.
            // This used to just clear the same priority but it might just need to clear all of them?
            // Current issue seen in 10.2 is that 10.1 Aspect Crest upgrade has a lower priority but also lower level.
            // Maybe just remove any base level lower than the target?
            newBonusIds = _.reject(
              newBonusIds,
              bid => baseLevelPriority >= GameData.BonusBaseLevels[bid]?.base_level_priority,
            );
            newBonusIds.push(bonusId);
            let newItem = setBonusIds(item, newBonusIds);
            newItem.itemLevel = itemLevel;
            newItem.upgradeCost = [
              // make sure we add to any previous upgrade costs here
              ...(newItem.upgradeCost || []),
              {
                itemId: reagentId,
                alternateItemId: CRAFTING_CURRENCY_ALTERNATE[reagentId]?.id,
                alternateItemAmount: CRAFTING_CURRENCY_ALTERNATE[reagentId]?.amount,
                amount: count,
                name: reagent.name,
                icon: reagent.icon,
              },
            ];

            // If an upgrade requires a Spark, it's probably a season transition and
            // any sockets won't be retained.
            if (getItemSlotName(item) !== 'neck' && _.includes(CRAFTING_SLOT_REMOVE_SOCKETS, slotId)) {
              newItem = removePrismaticSocket(newItem);
            }

            // god this is busted
            // should probably build a more general recursive solution?
            if (reagent.craftingPrereqs) {
              _.each(reagent.craftingPrereqs, craftingPrereq => {
                if (!hasBonusId(newItem, craftingPrereq.bonusId)) {
                  const prereqReagent = getReagent(craftingPrereq.itemId);
                  const prereqSlot = getSlotForReagent(craftingPrereq.itemId);

                  // TODO: What if there is no slot? Wouldn't that be weird?
                  const prereqItemSlot = _.find(
                    item.profession.optionalCraftingSlots,
                    s => s.id === prereqSlot.reagentSlotId,
                  );
                  const prereqCount = prereqItemSlot?.recraftCount || 1;

                  const newItemWithPrereqCost = addBonusIds(newItem, craftingPrereq.bonusId);

                  // need to make sure we make a new array here
                  newItemWithPrereqCost.upgradeCost = [...newItemWithPrereqCost.upgradeCost];
                  newItemWithPrereqCost.upgradeCost.push({
                    itemId: prereqReagent.id,
                    alternateItemId: CRAFTING_CURRENCY_ALTERNATE[prereqReagent.id]?.id,
                    alternateItemAmount: CRAFTING_CURRENCY_ALTERNATE[prereqReagent.id]?.amount,
                    amount: prereqCount,
                    name: prereqReagent.name,
                    icon: prereqReagent.icon,
                  });

                  upgrades.push(newItemWithPrereqCost);
                } else {
                  upgrades.push(newItem);
                }
              });
            } else {
              upgrades.push(newItem);
            }
          }
        }
      });
    }
  });

  return _.orderBy(upgrades, ['itemLevel'], ['desc']);
};

export const getItemConversion = (item, specId) => {
  if (!specId || !item) {
    return null;
  }

  const bonusIds = getBonusIds(item);
  const itemConversion = _.find(GameData.ItemConversions, conversion => {
    return _.size(_.intersection(bonusIds, conversion.bonusIds)) > 0;
  });
  if (itemConversion) {
    const possibleItems = getPossibleItemConversions(item, specId, itemConversion.id);
    if (_.size(possibleItems) === 0) {
      return null;
    }
    if (_.size(possibleItems) > 1) {
      throw new Error(
        `Found multiple possible item conversions. item: ${item.name} ${item.id} conversion ${itemConversion.id}`,
      );
    }
    const newItem = _.first(possibleItems);
    return newItem;
  }

  return null;
};

// TODO: Figure out where this is encoded in game data
// ItemBonusListGroup 296 looks to collect these in 10.1 but I can't figure out a map back to crafting
export const professionCraftingQualities = [{
  tier: 1,
  levelOffset: 0,
}, {
  tier: 2,
  levelOffset: 3,
}, {
  tier: 3,
  levelOffset: 6,
}, {
  tier: 4,
  levelOffset: 9,
}, {
  tier: 5,
  levelOffset: 13,
}];

const getCraftingQualityUpgrades = (item) => {
  const upgrades = [];
  if (item.crafting_quality) {
    const curTier = parseInt(item.crafting_quality, 10);
    const curLevelOffset = _.find(professionCraftingQualities, { tier: curTier })?.levelOffset || 0;

    const start = curTier + 1;
    const end = _.max(_.map(professionCraftingQualities, 'tier')) + 1;
    _.each(_.range(start, end), tier => {
      const newLevelOffset = _.find(professionCraftingQualities, { tier }).levelOffset;
      const diff = newLevelOffset - curLevelOffset;
      const newItem = updateItemLevel(item, item.itemLevel + diff);

      newItem.crafting_quality = tier;

      upgrades.push(newItem);
    });
  }
  return upgrades;
};

export const getItemMutations = (item, character, upgradeCurrencies) => {
  const mutations = [];

  const specInfo = getCharacterSpecInfo(character);

  // item upgrades
  const upgradeItems = getItemUpgrades(item, specInfo.id, character);
  _.each(upgradeItems, upgradeItem => {
    const upgrade = upgradeItem.upgrade;
    const affordable = _.every(upgradeItem.upgradeCost, cost => {
      return upgradeCurrencies[cost.currencyId || cost.itemId] >= cost.amount;
    });
    mutations.push({
      key: `${upgrade.group}-${upgrade.level}`,
      name: `Upgrade to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'item-upgrade'),
      type: 'item-upgrade',
      disabled: !affordable,
      disabledReason: 'Cannot afford',
    });
  });

  // crafting - item level upgrades
  const craftingLevelUpgrades = getCraftingLevelUpgrades(item);
  _.each(craftingLevelUpgrades, upgradeItem => {
    const affordable = _.every(upgradeItem.upgradeCost, cost => {
      const primaryAmount = upgradeCurrencies[cost.itemId];
      if (primaryAmount >= cost.amount) {
        return true;
      }
      const alternateAmount = upgradeCurrencies[cost.alternateItemId];
      if (alternateAmount && alternateAmount >= cost.alternateItemAmount) {
        return true;
      }

      return false;
    });

    mutations.push({
      key: _.join([upgradeItem.itemLevel, ..._.map(upgradeItem.upgradeCost, 'itemId')], '-'),
      name: `Upgrade to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'item-upgrade'),
      type: 'item-upgrade',
      disabled: !affordable,
      disabledReason: 'Cannot afford',
    });
  });

  const craftingQualityUpgrades = getCraftingQualityUpgrades(item);
  _.each(craftingQualityUpgrades, upgradeItem => {
    mutations.push({
      key: _.join(['craftingQuality', upgradeItem.itemLevel], '-'),
      name: `Recraft to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'recraft'),
      type: 'crafting-quality',
    });
  });


  // embellished
  const embellishments = getPossibleEmbellishments(item);
  if (embellishments) {
    const dedupedEmbels = _.map(_.groupBy(embellishments, 'name'), _.last);
    _.each(dedupedEmbels, embellishment => {
      // remove any existing embellishment bonus IDs
      mutations.push({
        name: `Embellishment: ${embellishment.name}`,
        item: addTag(replaceEmbellishment(item, embellishment.id), 'recraft'),
        type: 'embellishment',
      });
    });
    if (item.itemLimit) {
      mutations.push({
        name: 'Remove Embellishment',
        item: addTag(replaceEmbellishment(item, null), 'recraft'),
        type: 'embellishment',
      });
    }
  }

  // conversions (catalyst)
  const itemConversion = getItemConversion(item, specInfo.id);
  if (itemConversion) {
    mutations.push({
      name: 'Convert to Catalyst Item',
      item: addTag(itemConversion, 'catalyst'),
      type: 'catalyst',
    });
  }

  // recrafting stats
  const statRecrafts = getStatRecrafts(item);
  if (_.size(statRecrafts)) {
    _.each(statRecrafts, recraftItem => {
      mutations.push({
        name: `Recraft to ${recraftItem.statNames}`,
        item: addTag(recraftItem, 'recraft'),
        type: 'recraft',
      });
    });
  }

  // add socket(s)
  if (canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
    mutations.push({
      name: 'Add socket',
      item: addTag(addPrismaticSocket(item), 'add-socket'),
      type: 'add-socket',
    });

    if (item.source !== 'weekly_reward') {
      mutations.push({
        name: 'Add socket (Great Vault)',
        item: addTag(addTag(addPrismaticSocket(item), 'weekly_reward'), 'add-gv-socket'),
        type: 'add-socket',
      });
    }
  }

  const prismaticInfo = item.socketInfo[SOCKET_TYPES.PRISMATIC];
  if (prismaticInfo && prismaticInfo.dynamicSlots - prismaticInfo.staticSlots > 0) {
    mutations.push({
      name: 'Remove socket',
      item: addTag(removePrismaticSocket(item), 'remove-socket'),
      type: 'remove-socket',
    });
  }

  // remove gem
  if (item.gem_id) {
    mutations.push({
      name: 'Remove gem(s)',
      item: addTag({
        ...item,
        gem_id: undefined,
      }, 'remove-gem'),
      type: 'remove-gem',
    });
  }

  // remove enchant
  if (item.enchant_id) {
    mutations.push({
      name: 'Remove enchant',
      item: addTag({
        ...item,
        enchant_id: undefined,
      }, 'remove-enchant'),
      type: 'remove-enchant',
    });
  }

  return _.map(mutations, it => ({
    ...it,
    equipped: false,
  }));
};

const HIGH_SECONDARY_PROC_TRINKETS = [
  179350, // IQD
  207581, // Mirror of Fractured Tomorrow
];
export const isHighSecondaryProcTrinket = itemId => {
  return _.includes(HIGH_SECONDARY_PROC_TRINKETS, itemId);
};
